/**
 * Controlador PURO de la URL de reproducción firmada (F3-F).
 *
 * `GET /api/media/videos/:id/play?format=json` devuelve `{url, expiresAt}` con
 * TTL (6h). La URL EXPIRA → el `<video>` emite `error`; el player pide una URL
 * nueva y restaura la posición. Este controlador cachea la URL, sabe si está
 * expirada (con margen) y de-duplica peticiones concurrentes. La red se inyecta
 * (`fetcher`) → testeable con fake timers y mock, sin DOM ni `fetch` real.
 */

export interface PlayUrl {
  url: string;
  /** ISO-8601 de expiración (o epoch ms tolerado). */
  expiresAt: string;
}

export type PlayUrlFetcher = (videoId: string) => Promise<PlayUrl>;

export interface PlayUrlControllerOptions {
  /** Segundos antes de `expiresAt` en que ya se considera "por expirar". */
  skewSeconds?: number;
  /** Reloj inyectable (ms). Default `Date.now`. */
  now?: () => number;
}

export interface PlayUrlController {
  /** URL vigente (refresca si falta o expiró). De-dup de llamadas concurrentes. */
  get(): Promise<string>;
  /** Fuerza un refetch (tras un `error` del `<video>` por URL caducada). */
  refresh(): Promise<string>;
  /** URL cacheada sin tocar la red (o null). */
  peek(): string | null;
  /** ¿La URL cacheada está ausente o expirada (con margen)? */
  isStale(): boolean;
}

const DEFAULT_SKEW_SECONDS = 30;

function parseExpiry(expiresAt: string): number {
  const asNumber = Number(expiresAt);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    return asNumber;
  }
  const parsed = Date.parse(expiresAt);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function createPlayUrlController(
  videoId: string,
  fetcher: PlayUrlFetcher,
  options: PlayUrlControllerOptions = {},
): PlayUrlController {
  const skewMs = (options.skewSeconds ?? DEFAULT_SKEW_SECONDS) * 1000;
  const now = options.now ?? Date.now;

  let cached: PlayUrl | null = null;
  let expiryMs = 0;
  let inflight: Promise<string> | null = null;

  function isStale(): boolean {
    if (!cached) {
      return true;
    }
    return now() >= expiryMs - skewMs;
  }

  async function fetchFresh(): Promise<string> {
    const result = await fetcher(videoId);
    cached = result;
    expiryMs = parseExpiry(result.expiresAt);
    return result.url;
  }

  function get(): Promise<string> {
    if (cached && !isStale()) {
      return Promise.resolve(cached.url);
    }
    if (inflight) {
      return inflight;
    }
    inflight = fetchFresh().finally(() => {
      inflight = null;
    });
    return inflight;
  }

  function refresh(): Promise<string> {
    cached = null;
    expiryMs = 0;
    return get();
  }

  return {
    get,
    refresh,
    peek: () => cached?.url ?? null,
    isStale,
  };
}
